/**
 * Shared symbology rules for tactical line graphics: how a graphic's APP-6D
 * SIDC carries affiliation and status, and what colour that affiliation draws
 * in.
 *
 * THIS FILE IS THE SOURCE OF TRUTH. It is copied verbatim into each client:
 *
 *   ft-client-react/src/components/map/tactical/shared/symbology.js
 *   ft-android/app/src/main/assets/map/js/symbology.js
 *
 * (Same arrangement as tactical/attackLine.js and the CoT schema in /proto/ —
 * one authored copy in the umbrella repo, mechanically copied into the clients
 * that need it.)
 *
 * WHY THIS IS SHARED RATHER THAN PORTED
 * -------------------------------------
 * Affiliation is not decoration. A red line means "this is what the enemy is
 * doing" and a black one means "this is what we are doing"; a client that got
 * the mapping wrong would tell one player the opposite of what another sees,
 * off the same stored graphic. The SIDC digits that encode it, and the colours
 * they render as, therefore live here once instead of being re-derived per
 * client.
 *
 * WHY THERE IS NO AFFILIATION FIELD
 * ---------------------------------
 * The SIDC already carries it, at a position the standard fixes. Adding a
 * column would create a second answer that could disagree with the symbol
 * actually being drawn, and every consumer that renders from the SIDC (both
 * multipoint renderers, any TAK client reading our CoT) would ignore it anyway.
 */

/* eslint-disable strict -- see the wrapper note in attackLine.js: this file is
   also loaded as a classic <script> in the Android WebView, where the
   'use strict' directive is meaningful. A bundler sees a module and calls it
   redundant. */

(function (factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) {
        module.exports = api;
    } else if (typeof window !== 'undefined') {
        window.FTSymbology = api;
    }
}(function () {
    'use strict';

    /**
     * APP-6D digit positions, 0-based, in the 20/30-digit numeric format:
     *
     *   pos 0-1  version      pos 4-5  symbol set
     *   pos 2    context      pos 6    status
     *   pos 3    identity     pos 7    HQ/TF/dummy
     */
    var IDENTITY_INDEX = 3;
    var STATUS_INDEX = 6;

    /** Standard identity (position 3) values we name. */
    var IDENTITY_PENDING = '0';
    var IDENTITY_UNKNOWN = '1';
    var IDENTITY_ASSUMED_FRIEND = '2';
    var IDENTITY_FRIEND = '3';
    var IDENTITY_NEUTRAL = '4';
    var IDENTITY_SUSPECT = '5';
    var IDENTITY_HOSTILE = '6';

    /** Status (position 6) values we name. */
    var STATUS_PRESENT = '0';
    var STATUS_PLANNED = '1';

    /** Affiliation buckets — what a user actually distinguishes on the map. */
    var FRIEND = 'friend';
    var HOSTILE = 'hostile';
    var NEUTRAL = 'neutral';
    var UNKNOWN = 'unknown';

    /**
     * Identity digit -> affiliation bucket.
     *
     * Suspect collapses into hostile and assumed-friend into friend, matching
     * how the same digits already collapse to a CoT affiliation letter in
     * cot_utils (`5` and `6` both become `h`). A line drawn from a suspected
     * enemy is drawn in the enemy's colour: treating "suspect" as its own shade
     * would ask a player under time pressure to tell two reds apart.
     */
    var IDENTITY_TO_AFFILIATION = {
        '0': UNKNOWN,
        '1': UNKNOWN,
        '2': FRIEND,
        '3': FRIEND,
        '4': NEUTRAL,
        '5': HOSTILE,
        '6': HOSTILE
    };

    /**
     * Legacy MIL-STD-2525 letter affiliation (position 1 of a 15-char code) ->
     * APP-6D identity digit.
     *
     * Present because a parent entity may still carry a legacy code from older
     * data, and a line drawn from it must still inherit the right colour.
     * Exercise variants map to the real-world identity they stand in for — an
     * exercise enemy is still drawn as an enemy.
     */
    var LEGACY_AFFIL_TO_IDENTITY = {
        P: IDENTITY_PENDING,
        G: IDENTITY_PENDING,          // exercise pending
        U: IDENTITY_UNKNOWN,
        W: IDENTITY_UNKNOWN,          // exercise unknown
        A: IDENTITY_ASSUMED_FRIEND,
        M: IDENTITY_ASSUMED_FRIEND,   // exercise assumed friend
        F: IDENTITY_FRIEND,
        D: IDENTITY_FRIEND,           // exercise friend
        N: IDENTITY_NEUTRAL,
        L: IDENTITY_NEUTRAL,          // exercise neutral
        S: IDENTITY_SUSPECT,
        J: IDENTITY_SUSPECT,          // joker
        H: IDENTITY_HOSTILE,
        K: IDENTITY_HOSTILE           // faker
    };

    /**
     * Affiliation -> stroke/fill colour for a tactical line graphic.
     *
     * These are *ink* colours, not the APP-6D frame fills: a control measure is
     * a line drawn over terrain, so what matters is that it stays legible on a
     * satellite tile and that its affiliation is readable at a glance. Friendly
     * is black rather than the standard blue, which reads as a hand-drawn
     * overlay on a paper map; the other three keep the standard hues, darkened
     * from the APP-6D "light" values because pure green and pure yellow
     * disappear over pale terrain.
     *
     * Applied to graphics only. Point symbols (units, markers) keep milsymbol's
     * standard frame colours — their affiliation is already carried by the frame
     * shape, and recolouring those would be a change to NATO symbology rather
     * than to how our own drawings look.
     */
    var COLORS = {};
    COLORS[FRIEND] = '#000000';
    COLORS[HOSTILE] = '#E00000';
    COLORS[NEUTRAL] = '#00A000';
    COLORS[UNKNOWN] = '#E8C000';

    /** Fallback for a SIDC whose identity cannot be read at all. */
    var DEFAULT_COLOR = COLORS[FRIEND];

    /** True for the 20/30-digit APP-6D numeric format. */
    function isNumericSidc(sidc) {
        return typeof sidc === 'string' && sidc.length > STATUS_INDEX && /^\d+$/.test(sidc);
    }

    /**
     * The APP-6D identity digit of `sidc`, or null when it cannot be read.
     *
     * Accepts both the numeric APP-6D format and the legacy 15-character
     * letter format, so a graphic can inherit from any parent in the system.
     */
    function identityOf(sidc) {
        if (typeof sidc !== 'string' || sidc.length < 2) return null;
        if (isNumericSidc(sidc)) {
            var digit = sidc.charAt(IDENTITY_INDEX);
            return IDENTITY_TO_AFFILIATION[digit] ? digit : null;
        }
        var letter = sidc.charAt(1).toUpperCase();
        return LEGACY_AFFIL_TO_IDENTITY[letter] || null;
    }

    /** Affiliation bucket of `sidc`; unknown when the identity is unreadable. */
    function affiliationOf(sidc) {
        var identity = identityOf(sidc);
        return identity ? IDENTITY_TO_AFFILIATION[identity] : UNKNOWN;
    }

    /** Whether `sidc` denotes an enemy — hostile or suspect. */
    function isHostile(sidc) {
        return affiliationOf(sidc) === HOSTILE;
    }

    /** Stroke/fill colour a graphic with this SIDC draws in. */
    function colorOf(sidc) {
        var identity = identityOf(sidc);
        if (!identity) return DEFAULT_COLOR;
        return COLORS[IDENTITY_TO_AFFILIATION[identity]] || DEFAULT_COLOR;
    }

    /** Replace one digit, returning `sidc` untouched when it is not numeric. */
    function withDigit(sidc, index, digit) {
        if (!isNumericSidc(sidc)) return sidc;
        if (sidc.charAt(index) === digit) return sidc;
        return sidc.substring(0, index) + digit + sidc.substring(index + 1);
    }

    /** `sidc` with its standard identity (affiliation) set to `identity`. */
    function withIdentity(sidc, identity) {
        return withDigit(sidc, IDENTITY_INDEX, identity);
    }

    /** `sidc` with its status set to `status` (present / planned). */
    function withStatus(sidc, status) {
        return withDigit(sidc, STATUS_INDEX, status);
    }

    /**
     * The SIDC a graphic drawn from `parentSidc` should carry.
     *
     * Three rules, all about not making the user restate what the map already
     * knows:
     *
     *   1. The graphic inherits the parent's affiliation. A line drawn from an
     *      enemy marker describes enemy action, so it is an enemy graphic — the
     *      user chose that the moment they picked the marker, and asking again
     *      would only be a chance to get it wrong.
     *   2. An enemy graphic is **suspect** (identity 5), never confirmed hostile
     *      (6), however certain we are about the marker itself. Those two digits
     *      answer different questions: the marker's says how sure we are that
     *      *that is an enemy*, which someone may have confirmed by looking at
     *      it; the line's says how sure we are about *what it is going to do*,
     *      which nobody can confirm. Copying a confirmed 6 onto the line would
     *      launder a guess about intent into a statement of fact.
     *   3. An enemy graphic is Planned/Anticipated, not Present, for the same
     *      reason expressed in the status digit — and that is the digit the
     *      renderer draws dashed.
     *
     * Friendly and neutral graphics stay Present and keep their own identity: a
     * line drawn on our own marker is an order, not a guess. Returns `baseSidc`
     * unchanged when the parent's identity cannot be read, so an unrecognised
     * parent degrades to today's behaviour rather than to a wrong colour.
     */
    function forParent(baseSidc, parentSidc) {
        var identity = identityOf(parentSidc);
        if (!identity) return baseSidc;
        var enemy = IDENTITY_TO_AFFILIATION[identity] === HOSTILE;
        var sidc = withIdentity(baseSidc, enemy ? IDENTITY_SUSPECT : identity);
        return withStatus(sidc, enemy ? STATUS_PLANNED : STATUS_PRESENT);
    }

    return {
        IDENTITY_INDEX: IDENTITY_INDEX,
        STATUS_INDEX: STATUS_INDEX,
        IDENTITY_PENDING: IDENTITY_PENDING,
        IDENTITY_UNKNOWN: IDENTITY_UNKNOWN,
        IDENTITY_ASSUMED_FRIEND: IDENTITY_ASSUMED_FRIEND,
        IDENTITY_FRIEND: IDENTITY_FRIEND,
        IDENTITY_NEUTRAL: IDENTITY_NEUTRAL,
        IDENTITY_SUSPECT: IDENTITY_SUSPECT,
        IDENTITY_HOSTILE: IDENTITY_HOSTILE,
        STATUS_PRESENT: STATUS_PRESENT,
        STATUS_PLANNED: STATUS_PLANNED,
        FRIEND: FRIEND,
        HOSTILE: HOSTILE,
        NEUTRAL: NEUTRAL,
        UNKNOWN: UNKNOWN,
        COLORS: COLORS,
        DEFAULT_COLOR: DEFAULT_COLOR,
        identityOf: identityOf,
        affiliationOf: affiliationOf,
        isHostile: isHostile,
        colorOf: colorOf,
        withIdentity: withIdentity,
        withStatus: withStatus,
        forParent: forParent
    };
}));
